<template>
    <form @submit.prevent="submit">
        <div class="p-6 mb-6 shadow-md rounded-md">
            <div class="text-2xl leading-6 flex justify-between items-center mb-4">
                <h3 class="font-bold">{{ stepTitle }}</h3>
                <inertia-link
                    :href="route('tenant.survey.preview', {survey: surveyModel.id})"
                    title="Preview Survey"
                    class="mr-auto ml-7 flex items-center font-bold text-lg leading-6"
                    aria-label="Preview Survey"
                >
                    <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                    </svg>
                    Preview Survey
                </inertia-link>
                <p>Step {{ step }} of 5</p>
            </div>
            <div class="divide-y divide-y-2 divide-dark">
                <div v-for="(section, sectionIndex) in form.sections"
                     :key="'section-' + sectionIndex"
                     class="pb-2"
                     :class="sectionIndex === 0 ? '' : 'pt-7'"
                >
                    <h3 class="text-2xl font-bold" v-if="section.name">
                        {{ section.name}}
                    </h3>
                    <div class="divide-y divide-dark">
                        <div v-for="(question, questionIndex) in section.questions"
                             :key="'question-' + sectionIndex + '-' + questionIndex"
                             class="question pb-6"
                             :class="form.sections.length === 1 && questionIndex === 0 ? '' : 'pt-5'"
                        >
                            <h4 class="text-lg font-bold leading-6 mb-2">
                                {{ question.content }}
                            </h4>
                            <div class="mb-4 font-normal flex flex-wrap">
                                Answers:
                                <div v-for="(answer, answerIndex) in question.answers"
                                     :key="'answer-' + sectionIndex + '-' + questionIndex + '-' +answerIndex"
                                     class="rounded-md px-2 ml-2 text-lg font-bold"
                                     :style="{ color: answer.text_color, backgroundColor: answer.bg_color }"
                                >
                                    {{ answer.value }} <template v-if="answer.label"> ({{ answer.label }})</template>
                                    <!--<template v-if="answerIndex !== question.answers.length - 1">,</template>-->
                                </div>
                            </div>
                            <div class="text-sm">
                                <label class="flex items-center dark:text-gray-400">
                                    <jet-checkbox v-model:checked="question.allow_response_comment">
                                        Let the user add a comment
                                    </jet-checkbox>
                                </label>
                                <div v-if="question.allow_response_comment" class="custom-answer w-full py-4 px-6 bg-gray-100 dark:bg-gray-600 mt-2 rounded-md">
                                    <label class="block">
                                        <span class="block text-lg font-semibold leading-6 mb-2">Comment label</span>
                                        <jet-input v-model="question.response_comment_label" type="text" placeholder="eg: What can be improved?"
                                                   class="block w-full"/>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex justify-between">
            <jet-button @click="prevStep" :disabled="form.processing"
                        :bg-dark="true"
            >
                <svg class="mr-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                </svg>
                Previous step
            </jet-button>
            <jet-button @click="nextStep" :disabled="form.processing"
                        class="ml-auto"
                        :bg-dark="true"
            >
                Next step
                <svg class="ml-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                </svg>
            </jet-button>
        </div>
    </form>
</template>

<script>
import JetButton from "@/Jetstream/Button";
import JetInput from "@/Jetstream/Input";
import JetCheckbox from "@/Jetstream/Checkbox";
import {useForm} from "@inertiajs/inertia-vue3";
export default {
    name: "SurveyEditorStepThree",
    components: {JetButton, JetInput, JetCheckbox},
    emits: ['next-step', 'prev-step'],
    props: {
        surveyModel: {
            type: Object,
            required: true,
        },
        step: {
            type: Number,
            required: true
        },
        stepTitle: {
            type: String,
            required: true
        }
    },
    setup(props, { emit }) {
        const form = useForm({
            step: 3,
            next_step: 4,
            sections: props.surveyModel.sections,
        });

        const nextStep = () => {
            form.next_step = 4;
        }

        const prevStep = () => {
            form.next_step = 2;
        }

        const submit = () => {
            form.put(
                route('tenant.survey.update', {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}),
                {
                    onSuccess: () => {
                        if (form.next_step === 4) {
                            emit('next-step');
                        } else if (form.next_step === 2) {
                            emit('prev-step');
                        }
                    }
                }
            );
        }

        return {
            form,
            nextStep,
            prevStep,
            submit,
        }
    }
}
</script>

<style scoped>

</style>
